// PlanContextPanel — the shared "what you're buying" panel rendered beside the
// Stripe Embedded Checkout iframe, on BOTH surfaces that mount that checkout:
//   - the billing page's EmbeddedCheckoutModal (an existing org upgrading), and
//   - the in-onboarding SubscribeDriver (a brand-new paid-intent org).
//
// WHY THIS EXISTS (PE-1637): both surfaces must show the SAME price / fee /
// feature copy beside the payment form, and that copy must never drift. The fix
// is one derivation: this component turns (plan, feeMode, billingPeriod) into a
// <PlanCard compact> using only the shared billingPlans helpers — the exact
// numbers the funnel cards read. Neither caller hand-builds plan props anymore.
//
// SCOPE — DERIVED, NOT THE FUNNEL: at the point of purchase the plan is already
// chosen, so this is always presented as an UPGRADE to a paid tier (BASE/PLUS/
// PRO). It therefore renders the trial treatment when the engine says the tier
// trials, and never the cycle-switch / downgrade / current-plan framing the
// billing grid needs (that logic stays in BillingPlansPage, which owns the live
// subscription state). `compact` strips the funnel chrome (CTA, badge, expander)
// — the live Stripe form to the right IS the action.
//
// ENGINE CONTEXT: the compact PlanCard's `alreadyHasFeature` hides a feature
// group the VIEWING org already holds via a grandfather override. That requires
// the EntitlementsProvider's `can`, which both callers already have mounted
// above them (the billing page wraps its page; SubscribeDriver wraps itself).
// Callers pass `can` through as `alreadyHasFeature`. A brand-new onboarding org
// is implicit STARTER, so `can()` is false on every higher-tier key → nothing is
// hidden and the full feature list shows, which is exactly right at signup.

import type { Plan } from '@charitystack/entitlements';
import {
  planPriceCents,
  planPlatformFee,
  formatPriceCents,
  PLAN_PRESENTATION,
  stripSmsFromPresentation,
  resolveDynamicCopy,
  type BillingCycle,
  type FeeMode,
  type PlanPresentation,
} from '../../../../utils/billingPlans';
import { PlanCard, type BillingNote } from './PlanCard';

// PLAN_PRESENTATION is LAYERED: each tier's `groups` hold only what's net-new
// over the tier it `inheritFrom`s (Plus.inheritFrom = 'Base'; Base.inheritFrom =
// 'Starter'). So a tier's full feature set is its own groups PLUS every ancestor's
// up the chain — NOT just Starter's. Look up an ancestor by the display name in
// `inheritFrom` (e.g. 'Base' → the BASE presentation).
const PRESENTATION_BY_NAME: Record<string, PlanPresentation> = Object.fromEntries(
  Object.values(PLAN_PRESENTATION).map((p) => [p.name, p]),
);

// Build the FULL feature presentation for the checkout panel: the tier's own
// groups PLUS every inherited ancestor's groups, walking the inheritFrom chain
// (PE-1637). At the point of purchase the buyer wants to SEE the whole list, so
// we append each ancestor's actual feature groups below the tier's net-new ones,
// nearest ancestor first (Plus → Base → Starter). PlanCard renders each group as
// its own collapsed dropdown in compact mode (one disclosure per group), under an
// "Included features" heading — so the resting panel is short and the buyer
// expands the groups they care about.
//
// Walking the chain (not just appending STARTER) is load-bearing: appending only
// Starter to a PLUS card would omit BASE's groups entirely (the layer between
// Starter and Plus), silently showing an incomplete list at purchase. A `seen`
// guard makes a malformed/cyclic inheritFrom terminate rather than loop forever.
//
// inheritFrom is cleared so PlanCard does not also render the legacy
// "Everything in X, plus" static lead-in — the groups themselves are the list.
// SMS-stripping is applied to the COMPOSED whole by the caller.
//
// SUPERSESSION (billing-disclosure correctness): the layering assumes each tier's
// groups are net-new over its ancestors, but a higher tier sometimes REPLACES an
// ancestor capability rather than adding to it (Plus's uncapped marketing replaces
// Base's 4-broadcast cap; Pro's SMS allotment replaces Plus's). Appending the
// ancestor group verbatim would show a stale lower-tier limit beside the tier's own
// row — a contradiction on a purchase screen. A group declares the ancestor label(s)
// it supersedes via `supersedesGroups`. We collect that union across the WHOLE
// candidate chain FIRST (a superseding group's declaration still applies even if
// that group is itself superseded by a higher tier — so Base's cap drops in a Pro
// panel via Plus's declaration, transitively), then drop every group whose label is
// in the set. Match is by exact label.
function withInheritedFeatures(base: PlanPresentation): PlanPresentation {
  // 1. Walk the inheritFrom chain, collecting every candidate group (tier-own
  //    first, then nearest ancestor outward).
  const candidates = [...base.groups];
  const seen = new Set<string>([base.name]);
  let ancestorName = base.inheritFrom;
  while (ancestorName && !seen.has(ancestorName)) {
    seen.add(ancestorName);
    const ancestor = PRESENTATION_BY_NAME[ancestorName];
    if (!ancestor) break; // inheritFrom names a tier we don't have — stop gracefully.
    candidates.push(...ancestor.groups);
    ancestorName = ancestor.inheritFrom;
  }

  // 2. Union of all superseded ancestor labels declared anywhere in the chain.
  const superseded = new Set<string>();
  for (const g of candidates) {
    g.supersedesGroups?.forEach((label) => superseded.add(label));
  }

  // 3. Drop every group a higher tier supersedes (keep the superseding group).
  const groups = candidates.filter((g) => !superseded.has(g.label));
  return { ...base, inheritFrom: null, groups };
}

// The compact panel's sub-price line. Unlike the funnel grid, this panel sits
// beside the live Stripe Checkout form, which ALREADY states the trial and the
// first-charge date ("14 days free / Then $348.00 per year starting {date}"). So
// the panel does NOT repeat the trial here — that was redundant. Instead it makes
// the billing CADENCE legible right at the price: the big "$29" reads "/mo,
// billed annually" (via `unitSuffix`) and the annual total ("$348 per year") is
// the sub-line. This is the only situation the checkout panel ever shows: an
// upgrade to a paid tier. STARTER ($0) never reaches this panel (not a checkout
// target), but we guard it rather than divide by zero.
//
// All figures are engine-derived (planPriceCents → CATALOG cents), so the copy
// can't drift from what Stripe charges.
function checkoutBillingNote(plan: Plan, cycle: BillingCycle): BillingNote {
  const monthly = planPriceCents(plan, 'monthly');
  const yearly = planPriceCents(plan, 'yearly');
  if (monthly == null || monthly === 0) {
    return { text: 'Free forever · no card needed' };
  }

  // Annual cycle: "$29 /mo, billed annually" + "$348 per year". The cadence
  // qualifier rides the unit so it can't be missed; the annual total is the note.
  if (cycle === 'yearly' && yearly != null) {
    const annual = Math.round((yearly / 100) * 12);
    return { text: `$${annual} per year`, unitSuffix: ', billed annually' };
  }

  // Monthly cycle: the "/mo" is literal and self-explanatory; show the annualized
  // figure as context (no trial line — the Stripe form carries the trial).
  return { text: `$${Math.round((monthly / 100) * 12)}/yr if billed monthly` };
}

type PlanContextPanelProps = {
  // The paid tier being purchased. BASE/PLUS/PRO in practice (the only checkout
  // targets); typed as Plan so callers can pass their plan state without a cast.
  plan: Plan;
  // The org's fee mode — drives whether the fee line reads "Covered Commitment"
  // ($0 to you) or the Standard absorbed rate. Billing page: the live feeMode
  // state. Onboarding: 'commitment' (the engine default; nothing persisted yet).
  feeMode: FeeMode;
  // The billing cycle whose price/note to show. Billing page: the page toggle.
  // Onboarding: the resolved subscribe period (defaults monthly).
  billingPeriod: BillingCycle;
  // The EntitlementsProvider's `can`, used to hide a feature group a
  // grandfathered org already holds. Omitted → nothing hidden (full list shows).
  alreadyHasFeature?: (featureKey: string) => boolean;
  // Whether SMS surfaces are enabled (PocketFlows v1 = off). Off → the SMS copy
  // is stripped from the presentation, matching the billing page. Defaults off so
  // the panel never shows SMS copy that the rest of the product hides.
  smsEnabled?: boolean;
};

export function PlanContextPanel({
  plan,
  feeMode,
  billingPeriod,
  alreadyHasFeature,
  smsEnabled = false,
}: PlanContextPanelProps) {
  const priceCents = planPriceCents(plan, billingPeriod);
  // Compose the tier's groups WITH the inherited Starter baseline, then SMS-strip
  // the whole (so any SMS copy in either the tier's or Starter's groups is removed
  // together). The checkout panel shows the full feature list, not just "plus".
  const fullPresentation = withInheritedFeatures(PLAN_PRESENTATION[plan]);
  const presentation = smsEnabled
    ? resolveDynamicCopy(fullPresentation)
    : stripSmsFromPresentation(fullPresentation);

  return (
    <PlanCard
      plan={plan}
      presentation={presentation}
      priceLabel={formatPriceCents(priceCents)}
      showUnit={(priceCents ?? 0) > 0}
      billingNote={checkoutBillingNote(plan, billingPeriod)}
      feeMode={feeMode}
      feeCovered={planPlatformFee(plan, 'commitment')}
      feeUncovered={planPlatformFee(plan, 'standard')}
      // Checkout context: never the org's current plan, never the funnel
      // "recommended" badge — this is the chosen-and-being-purchased tier.
      isCurrent={false}
      isRecommended={false}
      // Always an upgrade at the point of purchase (drives the trial treatment in
      // the card). The live Stripe form is the action, so the CTA is hidden by
      // `compact` regardless of these.
      ctaKind="upgrade"
      ctaLabel=""
      onUpgrade={() => {}}
      pending={false}
      alreadyHasFeature={alreadyHasFeature}
      compact
    />
  );
}
